import * as THREE from 'three';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { VolumeRenderShader1 } from './shaders/VolumeShader.js';

export class TextureVisualization3D {

  constructor({
    widget,
    scaling = 1,
    cameraMode = 'perspective',
    heightExaggeration = 3,
    cameraPosition = [0, 0, 100],
    volconfig = {
      clim1: 0,
      clim2: 1,
      renderstyle: 'iso',
      isothreshold: 0.15,
      colormap: 'viridis'
    }
  }) {

    this.paused = false;
    if(widget){
      widget.addPauseHandler(this.pauseHandler.bind(this));
    }

    this.heightExaggeration = heightExaggeration;
    this.volconfig = volconfig;
    this.cameraMode = cameraMode;

    this.clock = new THREE.Clock();
    this.scene = new THREE.Scene();

    this.DOMElement = document.getElementById('volumetric');

    // Create renderer
    this.renderer = new THREE.WebGLRenderer();
    this.renderer.setPixelRatio(window.devicePixelRatio * scaling);
    this.sizeReference = this.DOMElement.parentNode.parentNode;
    this.renderer.setSize(this.sizeReference.clientWidth, this.sizeReference.clientHeight);
    this.DOMElement.appendChild(this.renderer.domElement);

    // Create camera (The volume renderer does not work very well with perspective yet)
    const aspect = this.sizeReference.clientWidth / this.sizeReference.clientHeight;
    if(this.cameraMode == 'orthographic'){
      const h = 512; // frustum height
      this.camera = new THREE.OrthographicCamera(- h * aspect / 2, h * aspect / 2, h / 2, - h / 2, 0.001, 1000);
    } else if(this.cameraMode == 'perspective'){
      this.camera = new THREE.PerspectiveCamera( 25, aspect, 50, 1e7 );
    }
    this.camera.position.set(
      cameraPosition[0],
      cameraPosition[1],
      cameraPosition[2]);
    this.camera.up.set(0, 0, 1); // In our data, z is up

    this.currentTexture = 0;
    this.textures = [];

    window.addEventListener('resize', this.onWindowResize.bind(this));

    this.animate();

  }

  pauseHandler(paused){
    this.paused = paused;
  }

  getDefaultController({x, y, z}){

    let controls = new OrbitControls(this.camera, this.renderer.domElement);
    controls.target.set(x/2, y/2, z/2);
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1.0;
    controls.minZoom = 0.5;
    controls.maxZoom = 4;
    controls.enablePan = true;
    controls.update();

    return controls;

  }

  setController(controls){
    this.controls = controls;
  }

  animate(){

    requestAnimationFrame(this.animate.bind(this));

    const fps = 30;

    var secs = this.clock.getDelta();

    if(this.controls){
      this.controls.autoRotateSpeed = 100 * secs;
      this.controls.update();
    }


    if(this.textures.length > 0 && !this.paused){

      let nextTexture = this.currentTexture + secs * fps;
      while(nextTexture >= this.textures.length){
        nextTexture -= this.textures.length;
      }

      if(Math.floor(this.currentTexture) != Math.floor(nextTexture)){
        this.material.uniforms['u_data'].value = this.textures[Math.floor(this.currentTexture)];
      }

      this.currentTexture = nextTexture;

    }

    this.renderer.render(this.scene, this.camera);

  }

  createTexture(volume){

    // Add another texture
    const texture = new THREE.Data3DTexture(volume.data, volume.xLength, volume.yLength, volume.zLength);
    texture.format = THREE.RedFormat;
    texture.type = THREE.UnsignedByteType;
    texture.minFilter = texture.magFilter = THREE.LinearFilter;
    texture.unpackAlignment = 1;
    texture.needsUpdate = true;

    // TODO sort by time
    this.textures.push(texture);

    return texture;

  }

  loadData(volume){

    if(this.textures.length > 0){

      this.createTexture(volume);

    } else {

      const texture = this.createTexture(volume);

      // Colormap textures
      this.cmtextures = {
        viridis: new THREE.TextureLoader().load('https://threejs.org/examples/textures/cm_viridis.png'),
        gray: new THREE.TextureLoader().load('https://threejs.org/examples/textures/cm_gray.png')
      };

      // Material
      const shader = VolumeRenderShader1;

      const uniforms = THREE.UniformsUtils.clone(shader.uniforms);

      uniforms['u_data'].value = texture;
      uniforms['u_size'].value.set(volume.xLength, volume.yLength, volume.zLength * this.heightExaggeration);
      uniforms['u_clim'].value.set(this.volconfig.clim1, this.volconfig.clim2);
      uniforms['u_renderstyle'].value = this.volconfig.renderstyle == 'mip' ? 0 : 1; // 0: MIP, 1: ISO
      uniforms['u_renderthreshold'].value = this.volconfig.isothreshold; // For ISO renderstyle
      uniforms['u_cmdata'].value = this.cmtextures[this.volconfig.colormap];

      this.material = new THREE.ShaderMaterial({
        uniforms: uniforms,
        vertexShader: shader.vertexShader,
        fragmentShader: shader.fragmentShader,
        side: THREE.BackSide, // The volume shader uses the backface as its "reference point"
        depthTest: false,
        transparent: true
      });

      const geometry = new THREE.BoxGeometry(volume.xLength, volume.yLength, volume.zLength * this.heightExaggeration);
      geometry.translate(volume.xLength / 2 - 0.5, volume.yLength / 2 - 0.5, volume.zLength * this.heightExaggeration / 2 - 0.5);

      const mesh = new THREE.Mesh(geometry, this.material);
      this.scene.add(mesh);

      this.geometry = geometry;
      this.mesh = mesh;

    }

  }

  updateUniforms() {

    this.material.uniforms['u_clim'].value.set(this.volconfig.clim1, this.volconfig.clim2);
    this.material.uniforms['u_renderstyle'].value = this.volconfig.renderstyle == 'mip' ? 0 : 1; // 0: MIP, 1: ISO
    this.material.uniforms['u_renderthreshold'].value = this.volconfig.isothreshold; // For ISO renderstyle
    this.material.uniforms['u_cmdata'].value = this.cmtextures[this.volconfig.colormap];

  }

  onWindowResize() {

    this.renderer.setSize(this.sizeReference.clientWidth, this.sizeReference.clientHeight);

    const aspect = this.sizeReference.clientWidth / this.sizeReference.clientHeight;

    if(this.cameraMode == 'orthographic'){
      const frustumHeight = this.camera.top - this.camera.bottom;
      this.camera.left = -frustumHeight * aspect / 2;
      this.camera.right = frustumHeight * aspect / 2;
    } else if(this.cameraMode == 'perspective'){
      this.camera.aspect = aspect;
    }

    this.camera.updateProjectionMatrix();

  }

}